import _ from "lodash";
import { useEffect, useState } from "react";
import { Button, Checkbox, Dropdown, Form, FormProps, Input, Modal, Segment, Table } from "semantic-ui-react";
import { ModalControlData, useModalControl } from "../hooks/commonHooks";
import { useFormConnect, useFormData } from "../hooks/formHooks";
import { restRequest } from "../http";
import { AREAS } from "../models/common_model";
import { PageData } from "../models/common_model";
import { USER_LANGUAGES, UserData } from "../models/user_model";
import { fieldError, formatDateTime } from "../utils";
import { Page } from "./commons/Page";
import { useCurrentUser } from "../hooks/auth-hook";

export function UserComponent(props: {}) {
    const [searchData, setSearchField] = useFormData<any>({});
    const [allRoles, setAllRoles] = useState<{ id: number, name: string }[]>();
    const [users, setSearchData] = useFormConnect<any, PageData<UserData>>({}, 'users/search');

    const editModalControl = useModalControl();
    const [currentEditId, setCurrentEditId] = useState<number>();
    const [reloadNumber, setReloadNumber] = useState(0);
    const currentUser = useCurrentUser();

    useEffect(() => {
        restRequest<{ id: number, name: string }[]>('roles/simple_list').then(setAllRoles);
    }, []);

    return <>
        <Segment.Group>
            <Segment attached="top">
                <Form onSubmit={e => setSearchData(searchData)}>
                    <Form.Group>
                        <Form.Field inline>
                            <Input placeholder="Name or Email" name="name" value={searchData['name'] || ''} onChange={setSearchField} />
                        </Form.Field>

                        <Form.Input inline>
                            <Dropdown placeholder="Is Admin" name="admin" value={searchData.admin} onChange={setSearchField} options={[{ key: 0, text: 'All' }, { key: 1, text: 'Yes', value: true }, { key: 2, text: 'No', value: false }]} search selection fluid></Dropdown>
                        </Form.Input>

                        <Form.Field inline>
                            <Dropdown placeholder="Role" name="role_ids" value={searchData.role_ids || ''} onChange={setSearchField} options={[{ key: 0, text: 'All' }].concat(allRoles?.map(r => ({ key: r.id, text: r.name, value: r.id })) || [])} search selection fluid></Dropdown>
                        </Form.Field>

                        <div><Button icon="search" type="submit"></Button></div>
                    </Form.Group>
                </Form>
            </Segment>
            <Segment attached="bottom">
                <Table striped>
                    <Table.Header>
                        <Table.Row>
                            <th>Email</th>
                            <th>Name</th>
                            <th>Is Admin</th>
                            <th>Role</th>
                            <th>RMA Areas</th>
                            <th>Products</th>
                            <th>Language</th>
                            <th>Created Time</th>
                            <th>{currentUser.permissions.user.create && <Button color="blue" icon="add" content="User" size="mini" onClick={e => {
                                setCurrentEditId(undefined);
                                editModalControl.setOpen(true);
                            }} />}</th>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {users?.items.map(user => {
                            return <tr key={user.id}>
                                <td>{user.email}</td>
                                <td>{user.name}</td>
                                <td>{user.admin ? 'Yes' : 'No'}</td>
                                <td>{user.roles.map(r => r.name).join(', ')}</td>
                                <td>{user.rma_areas.map(v => AREAS[v]).join(', ')}</td>
                                <td>{(user.products || []).join(', ')}</td>
                                <td>{USER_LANGUAGES.find(v => v.value === user.language)?.text || ''}</td>
                                <td>{formatDateTime(user.created_at)}</td>
                                <td>
                                    {currentUser.permissions.user.update && <Button onClick={e => {
                                        setCurrentEditId(user.id);
                                        editModalControl.setOpen(true);
                                    }} color="blue" icon="edit" size="mini" />}
                                </td>
                            </tr>
                        })}
                    </Table.Body>
                </Table>
                <Page data={users} updatePage={setSearchData}></Page>
            </Segment>
        </Segment.Group>

        <Editor id={currentEditId} allRoles={allRoles} modelControl={editModalControl} onFinish={result => {
            setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 }));
            editModalControl.setOpen(false)
            setReloadNumber(n => n + 1);
        }}></Editor>
    </>
}

function Editor(props: { id?: number, allRoles?: { id: number, name: string }[], modelControl: ModalControlData, onFinish: (data: UserData) => void }) {
    const { id, allRoles, modelControl, onFinish } = props;
    const isCreate = !id;

    const [data, setField, setData] = useFormData<UserData>({} as any);
    const [invalidError, setInvalidError] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState<string[]>();

    useEffect(() => {
        setData({admin: false, forcing_change_password: true} as any);
        setInvalidError({});
        if (id) {
            setLoading(true);
            restRequest<UserData>('users/' + id).then(setData).finally(() => setLoading(false))
        }
    }, [id]);

    useEffect(() => {
        restRequest<string[]>('svn_repository/products').then(setProducts)
    }, []);

    const onSubmit = (event: React.FormEvent<HTMLFormElement>, formProps: FormProps) => {
        event.preventDefault();

        setLoading(true);

        restRequest<UserData>(`users/${isCreate ? '' : id}`, data, isCreate ? 'post' : 'put').then(result => {
            props.onFinish(result);
            setInvalidError({});
        }, resp => {
            if (resp.status === 2) {
                setInvalidError(resp.data);
            }
        }).finally(() => setLoading(false));
    }


    return <>
        <Modal {...props.modelControl.props} size="large">
            <Modal.Header>{isCreate ? 'Create' : 'Update'} User </Modal.Header>
            <Modal.Content>
                <Form onSubmit={onSubmit} loading={loading}>

                    <Form.Group widths="equal">
                        <Form.Input label="RMA Area" required error={fieldError('rma_areas', invalidError)}>
                            <Dropdown multiple name="rma_areas" value={data['rma_areas'] || ''} onChange={setField} options={_.map(AREAS).map((name, value) => ({ key: value, text: name, value })) || []} search selection fluid></Dropdown>
                        </Form.Input>
                        <Form.Input label="Email" name="email" value={data['email'] || ''} onChange={setField} required error={fieldError('email', invalidError)}>
                        </Form.Input>
                        <Form.Input label="Name" name="name" value={data['name'] || ''} onChange={setField} required error={fieldError('name', invalidError)}>
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input label="Password" type="password" name="password" value={data['password'] || ''} onChange={setField} required={isCreate} error={fieldError('password', invalidError)}>
                        </Form.Input>
                        <Form.Input label="Confirm Password" type="password" name="confirm_password" value={data['confirm_password'] || ''} onChange={setField} required={isCreate} error={fieldError('confirm_password', invalidError)}>
                        </Form.Input>
                        <Form.Input label="Force Change Password">
                            <Checkbox checked={data.forcing_change_password} onChange={(e, d) => setField(null, {name: 'forcing_change_password', value: !data.forcing_change_password})}></Checkbox>
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input label="Is Admin" required error={fieldError('admin', invalidError)}>
                            <Dropdown name="admin" value={data.admin || false} onChange={setField} options={[{ key: 1, text: 'Yes', value: true }, { key: 2, text: 'No', value: false }]} search selection fluid></Dropdown>
                        </Form.Input>
                        <Form.Input label="Role" required={!data.admin} disabled={data.admin} error={fieldError('role_ids', invalidError)}>
                            <Dropdown multiple name="role_ids" value={data.role_ids || []} onChange={setField} options={allRoles?.map(r => ({ key: r.id, text: r.name, value: r.id })) || []} search selection fluid></Dropdown>
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input label="Allow Products">
                            <Dropdown multiple name="products" value={data.products || []} onChange={setField} options={products?.map(p => ({ key: p, text: p, value: p })) || []} search selection fluid></Dropdown>
                        </Form.Input>
                    </Form.Group>

                     <Form.Group widths="equal">
                        <Form.Input label="Language">
                            <Dropdown name="language" value={data.language || []} onChange={setField} options={USER_LANGUAGES} search selection fluid></Dropdown>
                        </Form.Input>
                    </Form.Group>
                    <div>
                        <Button primary>Submit</Button>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        <Button as="a" onClick={e => props.modelControl.setOpen(false)}>Cancel</Button>
                    </div>
                </Form>
            </Modal.Content>
        </Modal>
    </>
}